import { authAPI } from "./authApi";
import { resolveApiBaseUrl } from "./apiBase";

const API_BASE_URL = resolveApiBaseUrl();

export interface SearchItem {
  uid?: string;
  [key: string]: any;
}

export interface SearchResultEntry {
  ref: string | null;
  matchingField: string | null;
  matchingValue: string | null;
  item: SearchItem;
}

export interface SearchCollectionResult {
  collection: string;
  slug: string;
  title: string;
  count: number;
  results: SearchResultEntry[];
}

export interface SearchResponse {
  success: boolean;
  query: string;
  totalCount: number;
  collections: SearchCollectionResult[];
}

/** Max matches per collection returned by GET /search (backend caps at 100). */
export const SEARCH_PER_COLLECTION_LIMIT = 100;

export async function searchAll(
  query: string,
  perCollectionLimit: number = SEARCH_PER_COLLECTION_LIMIT,
  signal?: AbortSignal
): Promise<SearchCollectionResult[]> {
  const trimmed = query.trim();
  if (!trimmed) {
    return [];
  }

  const token = authAPI.getIdToken();

  const url = `${API_BASE_URL}/search?q=${encodeURIComponent(
    trimmed
  )}&perCollectionLimit=${encodeURIComponent(String(perCollectionLimit))}`;

  const response = await fetch(url, {
    method: "GET",
    headers: {
      "Content-Type": "application/json",
      ...(token && { Authorization: `Bearer ${token}` }),
    },
    signal,
  });

  const text = await response.text();

  if (!response.ok) {
    let message = text || `Search failed with status ${response.status}`;
    try {
      const parsed = text ? JSON.parse(text) : null;
      if (parsed && typeof parsed.message === "string") {
        message = parsed.message;
      }
    } catch {
      // ignore parse errors, keep message as-is
    }
    throw new Error(message);
  }

  if (!text) {
    return [];
  }

  let data: SearchResponse;
  try {
    data = JSON.parse(text) as SearchResponse;
  } catch (error) {
    throw new Error("Invalid search response from server");
  }

  if (!data.success || !Array.isArray(data.collections)) {
    return [];
  }

  return data.collections;
}

